/**
 * sellKindModules: which modules a ticked seller kind turns on.
 *
 * Split out of sellProfile.ts (logohome, 9 Oct 2026). The shell's module
 * list (`config/modules.ts`) needs only these pairs, but importing them from
 * sellProfile carried the whole "Are you any of these?" catalogue, its
 * labels, effects and codes, into every first load, and that load sat at its
 * 350 KB cap. The pairs live HERE and sellProfile's tables read them, so the
 * rule still has one home.
 */
import type { HealthPlace, SellerKind, SellFacts, TravelSell } from './sellProfile';

/** In catalogue order: `modulesForKinds` returns them in this order. */
export const KIND_MODULES: Partial<Record<SellerKind, string[]>> = {
    second_hand: ['inventory'],
    restaurant: ['restaurant', 'pos', 'inventory'],
    jeweller: ['inventory'],
    pharmacy: ['inventory'],
};

export const HEALTH_PLACE_MODULES: Record<HealthPlace, string[]> = {
    lab: ['hms', 'lab'],
    doctors: ['hms', 'hms-clinic'],
    scans: ['hms', 'hms-radiology'],
    cardiology: ['hms', 'hms-cardiology'],
    pharmacy: ['hms', 'hms-pharmacy'],
    beds: ['hms', 'hms-clinic', 'hms-inpatient', 'hms-insurance'],
};

export const TRAVEL_SELL_MODULES: Record<TravelSell, string[]> = {
    holidays: ['travel', 'travel-ops'],
    flights: ['travel'],
    hotels: ['travel', 'travel-ops'],
    visas: ['travel'],
    companies: ['travel', 'travel-corporate'],
    agents: ['travel', 'travel-partners'],
};

/**
 * The operational modules the ticked kinds need, once each, in catalogue order.
 * hms-2: a health business adds Karr HMS's Reception, and one department per
 * place it said it has (`facts.health.places`).
 */
export function modulesForKinds(kinds: readonly SellerKind[], facts?: SellFacts | null): string[] {
    const out: string[] = [];
    const add = (ms: readonly string[]) => { for (const m of ms) if (!out.includes(m)) out.push(m); };
    for (const [kind, ms] of Object.entries(KIND_MODULES) as [SellerKind, string[]][]) {
        if (kinds.includes(kind)) add(ms);
    }
    if (kinds.includes('health')) {
        add(['hms']);
        const places = facts?.health?.places ?? [];
        for (const [place, ms] of Object.entries(HEALTH_PLACE_MODULES) as [HealthPlace, string[]][]) {
            if (places.includes(place)) add(ms);
        }
    }
    // travel-2: a travel business adds Karr Travel, and the modules its
    // answers to "What do you sell?" need.
    if (kinds.includes('travel_agent')) {
        add(['travel']);
        const sells = facts?.travel_agent?.sells ?? [];
        for (const [sell, ms] of Object.entries(TRAVEL_SELL_MODULES) as [TravelSell, string[]][]) {
            if (sells.includes(sell)) add(ms);
        }
    }
    return out;
}
